// src/pages/ConclusionsListPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import {
  listConclusions,
  deleteConclusion,
  patchConclusionAccess,
  markConclusionPaid,
  unmarkConclusionPaid,
  resetConclusionDownloads,
  downloadConclusionPdf,
  saveConclusionPdf,
  getGenerationJobsStats,
} from '../api/results';
import { MAX_PUBLIC_DOWNLOADS, DOWNLOAD_LIMIT_OPTIONS } from '../constants';
import type { ConclusionListItem } from '../api/types';

const PageContainer = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => `${theme.spacing.xl} ${theme.spacing.lg}`};
  background: ${({ theme }) => theme.colors.background};
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  flex: 1;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const Select = styled.select`
  padding: 2px 5px;
  font-size: 0.8rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border-radius: 4px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin-right: 4px;
  accent-color: ${({ theme }) => theme.colors.secondary};
`;

const ActionBtn = styled.button`
  padding: 2px 8px;
  font-size: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border-radius: 4px;
  white-space: nowrap;
  opacity: ${({ disabled }) => disabled ? 0.4 : 1};
`;

const SavingMarker = styled.span`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.75rem;
  opacity: 0.7;
`;

const PaymentBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 1px 6px;
  font-size: 0.7rem;
  border-radius: 3px;
  font-weight: 600;
  background: ${({ status }) => status === 'paid' 
    ? '#4CAF50' 
    : '#FF9800'};
  color: ${({ status }) => status === 'paid' 
    ? '#ffffff' 
    : '#1b1b1b'};
`;

const Counter = styled.span<{ limit: number }>`
  font-size: 0.75rem;
  color: ${({ theme, limit }) => limit === 0 
    ? theme.colors.error 
    : theme.colors.text};
`;

const SourceBadge = styled.span<{ $missing?: boolean }>`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 0.7rem;
  border-radius: 3px;
  font-weight: 600;
  background: ${({ $missing }) => $missing ? '#f6ad55' : '#4CAF50'};
  color: ${({ $missing }) => $missing ? '#1b1b1b' : '#ffffff'};
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
`;

const JobsBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primary}18;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`;
const Tr = styled.tr`
  cursor: pointer;
  &:last-child td { border-bottom: none; }
  &:hover td { background: ${({ theme }) => theme.colors.background}; }
`;

/* ── Toast ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ToastMsg = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.colors.text};
  color: ${({ theme }) => theme.colors.cardBackground};
  padding: 10px 20px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
  z-index: 300;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  animation: ${fadeInUp} 0.25s ease-out;
  white-space: nowrap;
`;

/* ── Confirm modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ConfirmModal = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 420px;
  width: 100%;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;


const ConclusionsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [items, setItems] = useState<ConclusionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingLimit, setSavingLimit] = useState<number | null>(null);
  const [savingUnpaid, setSavingUnpaid] = useState<number | null>(null);
  const [savingPublic, setSavingPublic] = useState<number | null>(null);
  const [savingPaid, setSavingPaid] = useState<number | null>(null);
  const [savingUnmarkPaid, setSavingUnmarkPaid] = useState<number | null>(null);
  const [savingReset, setSavingReset] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [hideOrphans, setHideOrphans] = useState(false);
  const [codeFilter, setCodeFilter] = useState('');
  const [jobsStats, setJobsStats] = useState<{ queued: number; processing: number } | null>(null);

  /* ── Toast ── */
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string) => {
    setToastMsg(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(null), 3500);
  };

  /* ── Confirm modal ── */
  const [confirmState, setConfirmState] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const requestConfirm = (message: string, onConfirm: () => void) => setConfirmState({ message, onConfirm });

  const LIMIT_OPTIONS = DOWNLOAD_LIMIT_OPTIONS;
  const DEFAULT_LIMIT = MAX_PUBLIC_DOWNLOADS;

  const effectiveLimit = (item: ConclusionListItem): number => item.public_download_limit != null ? item.public_download_limit : DEFAULT_LIMIT;

  const handleLimitChange = async (item: ConclusionListItem, newValue: number) => {
    setSavingLimit(item.id);
    try {
      const result = await patchConclusionAccess(item.id, { public_download_limit: newValue });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        public_download_limit: typeof result.public_download_limit === 'number'
          ? result.public_download_limit
          : newValue,
        public_download_count: i.public_download_count,
      } : i));
    } catch {
      showToast(t('conclusionsErrorUpdateLimit'));
    } finally {
      setSavingLimit(null);
    }
  };

  const handleAllowUnpaidChange = async (item: ConclusionListItem, checked: boolean) => {
    setSavingUnpaid(item.id);
    try {
      const result = await patchConclusionAccess(item.id, { allow_unpaid: checked });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        allow_unpaid: typeof result.allow_unpaid === 'boolean' ? result.allow_unpaid : checked,
        public_download_count: i.public_download_count,
      } : i));
    } catch {
      showToast(t('conclusionsErrorUpdateUnpaid'));
    } finally {
      setSavingUnpaid(null);
    }
  };

  // Видимость заключения для внешнего пользователя (поиск по коду).
  const handlePublicChange = async (item: ConclusionListItem, checked: boolean) => {
    setSavingPublic(item.id);
    try {
      const result = await patchConclusionAccess(item.id, { is_public: checked });
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        is_public: typeof result.is_public === 'boolean' ? result.is_public : checked,
      } : i));
    } catch {
      showToast(t('conclusionsErrorUpdateVisibility'));
    } finally {
      setSavingPublic(null);
    }
  };

  const handleMarkPaid = async (item: ConclusionListItem, event: React.MouseEvent) => {
    event.stopPropagation();
    setSavingPaid(item.id);
    try {
      await markConclusionPaid(item.id);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, payment_status: 'paid', payment_source: 'manual' } : i));
    } catch {
      showToast(t('conclusionsErrorMarkPaid'));
    } finally {
      setSavingPaid(null);
    }
  };

  const handleUnmarkPaid = async (item: ConclusionListItem) => {
    setSavingUnmarkPaid(item.id);
    try {
      await unmarkConclusionPaid(item.id);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, payment_status: 'pending', payment_source: 'walletone' } : i));
      showToast(t('conclusionsUnmarkPaidDone'));
    } catch {
      showToast(t('conclusionsErrorMarkPaid'));
    } finally {
      setSavingUnmarkPaid(null);
    }
  };

  const handleResetDownloads = async (item: ConclusionListItem) => {
    setSavingReset(item.id);
    try {
      const result = await resetConclusionDownloads(item.id);
      const pubCount = typeof result.public_download_count === 'number' ? result.public_download_count : 0;
      const totalCount = typeof result.download_count === 'number' ? result.download_count : 0;
      setItems(prev => prev.map(i => i.id === item.id ? {
        ...i,
        public_download_count: pubCount,
        download_count: totalCount,
      } : i));
      const limit = typeof result.public_download_limit === 'number' ? result.public_download_limit : effectiveLimit(item);
      showToast(t('conclusionsResetDone').replace('{n}', String(pubCount)).replace('{m}', String(limit)));
    } catch {
      showToast(t('conclusionsErrorReset'));
    } finally {
      setSavingReset(null);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await deleteConclusion(id);
      setItems(prev => prev.filter(i => i.id !== id));
    } catch {
      showToast(t('conclusionsErrorDelete'));
    } finally {
      setDeleting(null);
    }
  };

  const deleteAllOrphans = () => {
    const orphans = items.filter(isOrphan);
    if (!orphans.length) return;
    requestConfirm(
      t('conclusionsConfirmDeleteOrphans').replace('{n}', String(orphans.length)),
      async () => {
        let ok = 0;
        for (const item of orphans) {
          try {
            await deleteConclusion(item.id);
            ok += 1;
          } catch {
            // продолжаем: часть могла уже быть удалена
          }
        }
        setItems(prev => prev.filter(i => !isOrphan(i)));
        showToast(ok > 0
          ? t('conclusionsDeletedOrphans').replace('{n}', String(ok))
          : t('conclusionsErrorDelete'));
      },
    );
  };

  const downloadPdf = async (item: ConclusionListItem) => {
    try {
      // PDF регистрируем при необходимости, затем скачиваем.
      if (!item.pdf_ready) {
        await saveConclusionPdf(item.id);
      }
      await downloadConclusionPdf(item.id);
    } catch {
      showToast(t('conclusionsPdfDownloadError'));
    }
  };

  const handleCodeClick = (event: React.MouseEvent, item: ConclusionListItem) => {
    event.preventDefault();
    // Заключение существует — скачиваем PDF (при необходимости формируем его).
    downloadPdf(item);
  };

  const isOrphan = (item: ConclusionListItem) => (item.missing_result_ids?.length ?? 0) > 0;
  const normalizedFilter = codeFilter.trim().toUpperCase();
  const visibleItems = (hideOrphans ? items.filter(i => !isOrphan(i)) : items)
    .filter(i => !normalizedFilter
      || (i.display_code || i.share_code || '').toUpperCase().includes(normalizedFilter)
      || (i.share_codes || []).some(c => c.toUpperCase().includes(normalizedFilter)));
  const orphanCount = items.filter(isOrphan).length;

  const isSaving = (itemId: number, field: string) => {
    switch (field) {
      case 'limit': return savingLimit === itemId;
      case 'unmarkPaid': return savingUnmarkPaid === itemId;
      case 'public': return savingPublic === itemId;
      case 'paid': return savingPaid === itemId;
      case 'reset': return savingReset === itemId;
      default: return false;
    }
  };

  useEffect(() => {
    listConclusions()
      .then(setItems)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  // Сколько запросов к LLM сейчас в очереди/обработке (показывается админу).
  useEffect(() => {
    const loadStats = () => {
      getGenerationJobsStats()
        .then(setJobsStats)
        .catch(() => setJobsStats(null));
    };
    loadStats();
    const timer = window.setInterval(loadStats, 10000);
    return () => window.clearInterval(timer);
  }, []);

  // Автообновление списка: статусы «Формируется…», лимиты и оплата меняются
  // воркером/внешним сервисом — без ручного F5.
  useEffect(() => {
    const reload = () => {
      listConclusions()
        .then(setItems)
        .catch(() => { /* таблица остаётся с прошлыми данными */ });
    };
    const timer = window.setInterval(reload, 15000);
    return () => window.clearInterval(timer);
  }, []);

  const preview = (item: ConclusionListItem): string => {
    const text = item.response_data?.text ?? item.response_data?.result ?? '';
    if (!text) return '—';
    return text.length > 120 ? text.slice(0, 120) + '…' : text;
  };

  return (
    <PageContainer>
        <Header>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>{t('backBtn')}</Button>
          <Title>{t('conclusionsPageTitle')}</Title>
        </Header>

        {loading && <p style={{ color: '#888' }}>{t('conclusionsLoading')}</p>}

        {!loading && loadError && (
          <p style={{ color: '#e53e3e' }}>{t('conclusionsLoadError')}</p>
        )}

        {!loading && !loadError && items.length === 0 && (
          <p style={{ color: '#888' }}>{t('conclusionsEmpty')}</p>
        )}

        {!loading && !loadError && items.length > 0 && (
          <>
            <Controls style={{ marginBottom: 8 }}>
              <Input
                value={codeFilter}
                onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder={t('conclusionsCodeSearch')}
                style={{ maxWidth: 260 }}
                fullWidth={false}
              />
              <FilterLabel>
                <input
                  type="checkbox"
                  checked={hideOrphans}
                  onChange={e => setHideOrphans(e.target.checked)}
                />
                {t('conclusionsHideOrphans')}
                {orphanCount > 0 && ` (${orphanCount})`}
              </FilterLabel>
              {jobsStats && (jobsStats.processing > 0 || jobsStats.queued > 0) && (
                <JobsBadge>
                  {t('conclusionsJobsProcessing').replace('{p}', String(jobsStats.processing))}
                  {' · '}
                  {t('conclusionsJobsQueued').replace('{q}', String(jobsStats.queued))}
                </JobsBadge>
              )}
              {orphanCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deleteAllOrphans}
                  style={{ color: '#e53e3e', borderColor: '#e53e3e' }}
                >
                  {t('conclusionsDeleteOrphans')}
                </Button>
              )}
            </Controls>
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>{t('conclusionsColType')}</Th>
                <Th>{t('conclusionsColCode')}</Th>
                <Th>{t('detailResults')}</Th>
                <Th>{t('conclusionsColDate')}</Th>
                <Th>{t('conclusionsColPreview')}</Th>
                <Th>{t('conclusionsColGeneration')}</Th>
                <Th>{t('conclusionsColDownloadLimit')}</Th>
                <Th>{t('conclusionsColVisibility')}</Th>
                <Th>{t('conclusionsColAllowUnpaid')}</Th>
                <Th>{t('conclusionsColPayment')}</Th>
                <Th>{t('conclusionsColPublicCount')}</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {visibleItems.map(item => (
                <Tr key={item.id}>
                  <Td>{item.id}</Td>
                  <Td>{item.type === 'individual' ? t('conclusionsTypeIndividual') : item.type === 'group' ? t('conclusionsTypeGroup') : item.type === 'comparison' ? t('conclusionsTypeComparison') : item.type}</Td>
                  <Td style={{ fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                    {item.display_code || item.share_code ? (
                      <a
                        className="code"
                        href={item.pdf_url || `/conclusion/${item.id}`}
                        onClick={e => handleCodeClick(e, item)}
                        title={t('conclusionsPdfDownloadTitle')}
                      >
                        {item.display_code || item.share_code}
                      </a>
                    ) : (
                      <span style={{ color: '#888' }}>—</span>
                    )}
                    {item.share_codes?.length > 0 && (
                      <SourceBadge>{t('conclusionsSourceExist')}</SourceBadge>
                    )}
                    {isOrphan(item) && item.share_codes?.length > 0 && (
                      <SourceBadge $missing>
                        {t('conclusionsSourcePartialMissing').replace('{ids}', (item.missing_result_ids || []).join(', '))}
                      </SourceBadge>
                    )}
                    {isOrphan(item) && (item.share_codes?.length ?? 0) === 0 && (
                      <SourceBadge $missing>
                        {t('conclusionsSourceMissing').replace('{ids}', (item.missing_result_ids || []).join(', '))}
                      </SourceBadge>
                    )}
                  </Td>
                  <Td>{(item.result_ids || []).join(', ')}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>
                    {item.created_at ? new Date(item.created_at).toLocaleString() : '—'}
                  </Td>
                  <Td style={{ maxWidth: '280px', color: '#888', fontSize: '0.82rem' }}>{preview(item)}</Td>
                  <Td style={{ whiteSpace: 'nowrap' }}>
                    {item.generation_status === 'processing'
                      ? <JobsBadge>{t('conclusionsColGenerating')}</JobsBadge>
                      : '—'}
                  </Td>
                  <Td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <Select
                      value={effectiveLimit(item)}
                      onChange={e => {
                        handleLimitChange(item, parseInt(e.target.value));
                      }}
                      disabled={isSaving(item.id, 'limit')}
                    >
                      {LIMIT_OPTIONS.map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </Select>
                    {isSaving(item.id, 'limit') && <SavingMarker>…</SavingMarker>}
                  </Td>
                  <Td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={item.is_public !== false}
                      onChange={e => {
                        handlePublicChange(item, e.target.checked);
                      }}
                      disabled={isSaving(item.id, 'public')}
                    />
                    {isSaving(item.id, 'public') && <SavingMarker>…</SavingMarker>}
                  </Td>
                  <Td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={!!item.allow_unpaid}
                      onChange={e => {
                        handleAllowUnpaidChange(item, e.target.checked);
                      }}
                      disabled={isSaving(item.id, 'unpaid')}
                    />
                    {isSaving(item.id, 'unpaid') && <SavingMarker>…</SavingMarker>}
                  </Td>
                  <Td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <PaymentBadge status={item.payment_status || 'pending'}>
                      {item.payment_status === 'paid'
                        ? (item.payment_source === 'manual' ? t('conclusionsPaymentManual') : item.payment_source)
                        : t('conclusionsPaymentNone')}
                    </PaymentBadge>
                    {' '}
                    {item.payment_status !== 'paid' && (
                      <ActionBtn
                        onClick={e => handleMarkPaid(item, e)}
                        disabled={isSaving(item.id, 'paid')}
                        title={t('conclusionsBtnPayTitle')}
                      >
                        {isSaving(item.id, 'paid') ? '…' : t('conclusionsBtnPay')}
                      </ActionBtn>
                    )}
                    {item.payment_status === 'paid' && item.payment_source === 'manual' && (
                      <ActionBtn
                        onClick={e => {
                          e.stopPropagation();
                          requestConfirm(
                            t('conclusionsConfirmUnmarkPaid'),
                            () => handleUnmarkPaid(item),
                          );
                        }}
                        disabled={isSaving(item.id, 'unmarkPaid')}
                        title={t('conclusionsBtnUnmarkPaidTitle')}
                      >
                        {isSaving(item.id, 'unmarkPaid') ? '…' : t('conclusionsBtnUnmarkPaid')}
                      </ActionBtn>
                    )}
                  </Td>
                  <Td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    <Counter limit={effectiveLimit(item)}>
                      {t('conclusionsDownloadedCount')
                        .replace('{n}', String(item.public_download_count || 0))
                        .replace('{m}', String(effectiveLimit(item)))}
                    </Counter>
                    {' '}
                    <ActionBtn
                      onClick={() => requestConfirm(
                        t('conclusionsConfirmReset'),
                        () => handleResetDownloads(item),
                      )}
                      disabled={isSaving(item.id, 'reset')}
                      title={t('conclusionsBtnResetTitle')}
                    >
                      {isSaving(item.id, 'reset') ? '…' : '↺'}
                    </ActionBtn>
                  </Td>
                  <Td onClick={e => e.stopPropagation()} style={{ width: 40 }}>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        requestConfirm(t('conclusionsConfirmDelete'), () => handleDelete(item.id));
                      }}
                      disabled={deleting === item.id}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', fontSize: '1rem', padding: '2px 6px', opacity: deleting === item.id ? 0.4 : 1 }}
                      title={t('conclusionsBtnDeleteTitle')}
                    >✕</button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
          </>
        )}

        {!loading && !loadError && items.length > 0 && visibleItems.length === 0 && (
          <p style={{ color: '#888', marginTop: 8 }}>{t('conclusionsNoVisible')}</p>
        )}

        {/* ── Toast ── */}
        {toastMsg && <ToastMsg>{toastMsg}</ToastMsg>}

        {/* ── Confirm modal ── */}
        {confirmState && (
          <Overlay onClick={() => setConfirmState(null)}>
            <ConfirmModal onClick={e => e.stopPropagation()}>
              <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{confirmState.message}</p>
              <ConfirmActions>
                <Button variant="outline" size="sm" onClick={() => setConfirmState(null)}>
                  {t('cancelButton')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    confirmState.onConfirm();
                    setConfirmState(null);
                  }}
                >
                  {t('conclusionsConfirmAction')}
                </Button>
              </ConfirmActions>
            </ConfirmModal>
          </Overlay>
        )}
    </PageContainer>
  );
};

export default ConclusionsListPage;
